import { getCookie } from '@alexis/helpers/cookie';
import { toISOStringWithTimezone } from '@alexis/helpers/date';
import { genzoTrackPageView } from '@alexis/helpers/genzo';
import { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import Drawer from '@components/Drawer';

import { GlobalContext } from '@context/GlobalContext';

import { isGoogleBot } from '@helpers/bots';

import HamburgerMenuIcon from '@icons/hamburger-menu.svg';

import {
  getCurrentSiteState,
  getLocaleState,
  getTranslationsState,
  getUserState,
} from '@redux/selectors';

import { CurrentSite } from '@wegoTypes/currentSite';

import styles from './PreferencesLayout.module.scss';
import PreferencesSideNavigation from './PreferencesSideNavigation';

type PreferencesLayoutProps = {
  children: React.ReactNode;
  pageViewId: string;
  localeParam?: string;
};

const PreferencesLayout: React.FC<PreferencesLayoutProps> = ({
  children,
  pageViewId,
  localeParam,
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const translations = useSelector(getTranslationsState);

  const wegoAnalyticsClientId = getCookie('wego_analytics_client_id');
  const wegoAnalyticsClientSessionId = getCookie('wego_analytics_client_session_id');
  const { webEngageAnonymousId } = useContext(GlobalContext);

  const user = useSelector(getUserState);
  const locale = useSelector(getLocaleState);
  const currentSite: CurrentSite = useSelector(getCurrentSiteState);

  useEffect(() => {
    if (
      !isGoogleBot &&
      !!wegoAnalyticsClientId &&
      !!wegoAnalyticsClientSessionId &&
      !!webEngageAnonymousId &&
      !!pageViewId
    ) {
      const clientData: any = {
        id: wegoAnalyticsClientId,
        session_id: wegoAnalyticsClientSessionId,
      };

      const pageData: any = {
        base_type: 'account',
        sub_type: 'dashboard',
        product: '',
        locale: locale,
        site_code: currentSite.countryCode,
        url: window.location.href,
        referrer_url: document.referrer,
      };

      const data: any = {
        id: pageViewId,
        client: clientData,
        page: pageData,
        created_at: toISOStringWithTimezone(new Date()),
        external_services: {
          crm_id: user ? user.userHash : webEngageAnonymousId,
        },
      };

      if (!!user) {
        data.client.user_hash = user.userHash;
      }
      genzoTrackPageView(API_BASE_URL, data);
    }
  }, [
    locale,
    pageViewId,
    wegoAnalyticsClientId,
    wegoAnalyticsClientSessionId,
    webEngageAnonymousId,
  ]);

  return (
    <div className={styles.container}>
      <Drawer open={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} title='Menu'>
        <PreferencesSideNavigation localeParam={localeParam} pageViewId={pageViewId} />
      </Drawer>
      <div className={styles.layout}>
        <aside className={styles.sidebar}>
          <PreferencesSideNavigation localeParam={localeParam} pageViewId={pageViewId} />
        </aside>
        <main className={styles.content}>
          <button className={styles.hamburgerButton} onClick={() => setIsDrawerOpen(true)}>
            <HamburgerMenuIcon />
            <span>{translations.menu}</span>
          </button>
          {children}
        </main>
      </div>
    </div>
  );
};

export default PreferencesLayout;
